import React from 'react';
import clsx from 'clsx';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  AgencySubscriptionFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  SubscriptionPeriod,
  SubscriptionPlan
} from '~/api/graphql';

import {
  ANNUAL_RENEWAL_PLAN_LABELS,
  RENEWAL_CALL_URL,
  RENEWAL_PLAN_OPTIONS,
  STANDARD_PLAN_TERMS_URL,
  formatAnnualRenewalDate,
  formatBillingPeriod,
  getAnnualRenewalDate
} from './annual-renewal';
import {
  finalizeSubscriptionCheckout,
  previewSubscriptionCheckout,
  SubscriptionCheckoutInput,
  SubscriptionCheckoutPreview
} from './complete-subscription-checkout';
import { confirmAnnualRenewal } from '~/api/data/billing/mutations';
import { InvoicePreview, InvoicePreviewSkeleton } from './invoice-preview';
import { RenewalMigrationPaymentForm } from './renewal-migration-payment-form';
import { formatUsd } from '~/components/settings/usage/usage-limits-utils';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const stripePublicKey = process.env.STRIPE_PUBLIC_KEY || window.oyster?.opts?.stripePublicKey || '';
const stripePromise = loadStripe(stripePublicKey);

const LINK_CLASS =
  'font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:hover:text-sky-200 dark:text-sky-400';

type RenewalSelection = Pick<SubscriptionCheckoutInput, 'plan' | 'period' | 'seats'>;

const currentSelection = (
  subscription: Pick<AgencySubscriptionFieldsFragment, 'plan' | 'period' | 'numSeats'>
): RenewalSelection => ({
  plan: subscription.plan ?? SubscriptionPlan.Pro,
  period: subscription.period ?? SubscriptionPeriod.Monthly,
  seats: subscription.numSeats
});

const isSameSelection = (a: RenewalSelection, b: RenewalSelection) =>
  a.plan === b.plan && a.period === b.period && a.seats === b.seats;

/**
 * Stripe pricing for a changed selection. Only requested while enabled, so an
 * admin who keeps the plan as it is never waits on Stripe.
 */
const useCheckoutPreview = (enabled: boolean, input: SubscriptionCheckoutInput) => {
  const requestIdRef = React.useRef(0);
  const [preview, setPreview] = React.useState<SubscriptionCheckoutPreview | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const load = React.useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(undefined);
    const result = await previewSubscriptionCheckout(input);
    if (requestId !== requestIdRef.current) {
      return;
    }
    setPreview(result.preview);
    setError(result.preview ? undefined : (result.error ?? 'Unable to load pricing.'));
    setLoading(false);
  }, [input]);

  React.useEffect(() => {
    if (!enabled) {
      requestIdRef.current++;
      setPreview(null);
      setLoading(false);
      setError(undefined);
      return;
    }
    void load();
  }, [enabled, load]);

  return { preview, loading, error, reload: load };
};

/**
 * Applies a changed selection through updateSubscription, then records the
 * confirmation. An unchanged plan skips Stripe entirely: it already renews.
 */
const useConfirmRenewal = ({
  open,
  changed,
  checkoutInput,
  preview,
  onDone
}: {
  open: boolean;
  changed: boolean;
  checkoutInput: SubscriptionCheckoutInput;
  preview: SubscriptionCheckoutPreview | null;
  onDone: () => void;
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string>();

  React.useEffect(() => {
    if (open) {
      setSubmitError(undefined);
    }
  }, [open]);

  const confirmUnchanged = async () => {
    try {
      const { error } = await confirmAnnualRenewal();
      if (error) throw error;
      onDone();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to confirm renewal.');
    }
  };

  // updateSubscription records the confirmation itself, so a successful
  // change needs no separate confirm call.
  const applyChange = async () => {
    if (!preview) {
      return;
    }
    const result = await finalizeSubscriptionCheckout(checkoutInput, preview);
    if (result.status === 'error') {
      setSubmitError(result.error ?? 'Unable to update your plan.');
    }
    if (result.status === 'updated') {
      onDone();
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      await (changed ? applyChange() : confirmUnchanged());
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, submitError, handleConfirm };
};

export const AnnualRenewalWizard = (props: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) => {
  const subscription = props.agencyUser.agency.subscription;
  if (!subscription?.annualRenewalReviewOpen) {
    return null;
  }
  return <AnnualRenewalDialog {...props} subscription={subscription} />;
};

const AnnualRenewalDialog = ({
  open,
  onClose,
  onCompleted,
  agencyUser,
  subscription
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  subscription: AgencySubscriptionFieldsFragment;
}) => {
  const { plan, period, numSeats } = subscription;
  const current = React.useMemo(
    () => currentSelection({ plan, period, numSeats }),
    [plan, period, numSeats]
  );
  const [selection, setSelection] = React.useState(current);
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelection(current);
      setAgreedToTerms(false);
    }
  }, [open, current]);

  const changed = !isSameSelection(selection, current);
  const checkoutInput = React.useMemo(
    () => ({ ...selection, voiceAgents: Math.max(0, subscription.numVoiceAgents - 1) }),
    [selection, subscription.numVoiceAgents]
  );
  const checkout = useCheckoutPreview(open && changed, checkoutInput);
  const onDone = () => {
    onCompleted?.();
    onClose();
  };
  const { submitting, submitError, handleConfirm } = useConfirmRenewal({
    open,
    changed,
    checkoutInput,
    preview: checkout.preview,
    onDone
  });

  const needsEmbeddedPayment = changed && !!checkout.preview?.clientSecret;
  const canConfirm =
    agreedToTerms &&
    !submitting &&
    (!changed || (!checkout.loading && !!checkout.preview?.canChargePaymentMethodOnFile));
  const renewalDate = getAnnualRenewalDate(subscription);

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} size="3xl">
      <DialogTitle>Review your renewal</DialogTitle>
      <DialogDescription>
        {renewalDate
          ? `${agencyUser.agency.name} renews on ${formatAnnualRenewalDate(renewalDate)} for a 12 month term.`
          : `${agencyUser.agency.name} renews for a 12 month term.`}{' '}
        Keep your plan as it is, or make changes below.
      </DialogDescription>

      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <PlanField
              currentPlan={current.plan}
              plan={selection.plan}
              onChange={(plan) => setSelection((s) => ({ ...s, plan }))}
            />
            <div className="grid gap-x-6 gap-y-6 sm:grid-cols-2">
              <BillingCycleField
                period={selection.period}
                onChange={(period) => setSelection((s) => ({ ...s, period }))}
              />
              <SeatsField
                seats={selection.seats}
                minSeats={current.seats}
                onChange={(seats) => setSelection((s) => ({ ...s, seats }))}
              />
            </div>
          </FieldGroup>
        </Fieldset>

        {changed ? (
          <ChangePreview checkout={checkout} agencyUser={agencyUser} />
        ) : (
          <CurrentPlanSummary subscription={subscription} />
        )}

        <TermsCheckbox checked={agreedToTerms} onChange={setAgreedToTerms} />

        {needsEmbeddedPayment && agreedToTerms && checkout.preview?.clientSecret && (
          <div className="mt-6">
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: checkout.preview.clientSecret }}
            >
              <RenewalMigrationPaymentForm
                agencyUser={agencyUser}
                plan={selection.plan}
                period={selection.period}
                seats={selection.seats}
                voiceAgents={checkoutInput.voiceAgents}
                onSuccess={onDone}
              />
            </Elements>
          </div>
        )}

        <QuestionsFooter />

        {submitError && (
          <div className="mt-4">
            <ErrorMessage>{submitError}</ErrorMessage>
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button color="white" type="button" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {!needsEmbeddedPayment && (
          <Button color="sky" loading={submitting} disabled={!canConfirm} onClick={handleConfirm}>
            Confirm renewal
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** Basic admins may upgrade to Pro; Pro admins are pointed at Enterprise instead. */
const PlanField = ({
  currentPlan,
  plan,
  onChange
}: {
  currentPlan: SubscriptionPlan;
  plan: SubscriptionPlan;
  onChange: (plan: SubscriptionPlan) => void;
}) => {
  if (currentPlan !== SubscriptionPlan.Basic) {
    return (
      <Field>
        <Label>Plan</Label>
        <Description>
          You are on {ANNUAL_RENEWAL_PLAN_LABELS[currentPlan]}. If you&apos;re interested in
          learning more about broader customization options and workflows available with Cara
          Enterprise,{' '}
          <a
            href={RENEWAL_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            contact our team
          </a>
          .
        </Description>
      </Field>
    );
  }

  return (
    <Field>
      <Label>Plan</Label>
      <Description>
        Stay on Cara Basic, or upgrade to Cara Pro for the full feature set.
      </Description>
      <div
        data-slot="control"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        role="radiogroup"
        aria-label="Plan"
      >
        {RENEWAL_PLAN_OPTIONS.map((option) => (
          <label
            key={option.id}
            className={clsx(
              'group/tier flex cursor-pointer flex-col rounded-xl p-4 ring-1 ring-neutral-200 dark:bg-neutral-800/50 dark:ring-white/15',
              plan === option.id && 'ring-primary-600 ring-2 dark:ring-primary-400'
            )}
          >
            <input
              type="radio"
              name="annual-renewal-plan"
              value={option.id}
              checked={plan === option.id}
              onChange={() => onChange(option.id)}
              className="sr-only"
            />
            <h3 className="m-0 p-0 text-base/7 font-semibold text-neutral-900 dark:text-white">
              {option.name}
            </h3>
            <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-300">
              {option.description}
            </p>
          </label>
        ))}
      </div>
    </Field>
  );
};

const BILLING_CYCLE_OPTIONS = [
  { value: SubscriptionPeriod.Monthly, label: 'Monthly' },
  { value: SubscriptionPeriod.Annual, label: 'Annual' }
];

const BillingCycleField = ({
  period,
  onChange
}: {
  period: SubscriptionPeriod;
  onChange: (period: SubscriptionPeriod) => void;
}) => (
  <Field>
    <Label>Billing Cycle</Label>
    <Description>Choose how often you are billed during the next 12 months.</Description>
    <div data-slot="control">
      <fieldset
        aria-label="Payment frequency"
        className="inline-flex rounded-full bg-neutral-100 p-1 dark:bg-neutral-800"
      >
        {BILLING_CYCLE_OPTIONS.map((option) => (
          <label key={option.value} className="relative cursor-pointer">
            <input
              type="radio"
              name="annual-renewal-billing-cycle"
              value={option.value}
              checked={period === option.value}
              onChange={() => onChange(option.value)}
              aria-label={option.label}
              className="sr-only peer"
            />
            <span className="block rounded-full px-4 py-1.5 text-xs font-medium text-neutral-700 peer-checked:text-white peer-checked:bg-primary-600 peer-checked:shadow-sm dark:text-neutral-300 dark:peer-checked:text-white">
              {option.label}
            </span>
          </label>
        ))}
      </fieldset>
    </div>
  </Field>
);

const STEPPER_BUTTON_CLASS =
  'flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700';
const STEPPER_BUTTON_DISABLED_CLASS =
  'flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600';

/** Seats can be added at renewal but not removed below the current count. */
const SeatsField = ({
  seats,
  minSeats,
  onChange
}: {
  seats: number;
  minSeats: number;
  onChange: (seats: number) => void;
}) => (
  <Field>
    <Label>Seats</Label>
    <Description>
      You have {minSeats} {minSeats === 1 ? 'seat' : 'seats'} today. You can add seats but not
      remove them. Reach out to{' '}
      <a href={RENEWAL_CALL_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
        support
      </a>{' '}
      for questions.
    </Description>
    <div data-slot="control" className="flex items-center gap-3">
      <button
        type="button"
        aria-label="Decrease seats"
        onClick={() => onChange(Math.max(minSeats, seats - 1))}
        disabled={seats <= minSeats}
        className={seats <= minSeats ? STEPPER_BUTTON_DISABLED_CLASS : STEPPER_BUTTON_CLASS}
      >
        <MinusIcon className="h-4 w-4" />
      </button>
      <div className="w-24">
        <Input
          type="number"
          min={minSeats}
          value={seats}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (!isNaN(value) && value >= minSeats) {
              onChange(value);
            }
          }}
          className="w-full text-center"
        />
      </div>
      <button
        type="button"
        aria-label="Increase seats"
        onClick={() => onChange(seats + 1)}
        className={STEPPER_BUTTON_CLASS}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  </Field>
);

const CurrentPlanSummary = ({
  subscription
}: {
  subscription: AgencySubscriptionFieldsFragment;
}) => (
  <div className="mt-8 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 dark:border-neutral-700 dark:bg-neutral-800/50">
    <p className="m-0 text-sm font-medium text-neutral-900 dark:text-white">
      {subscription.plan && ANNUAL_RENEWAL_PLAN_LABELS[subscription.plan]} · {subscription.numSeats}{' '}
      {subscription.numSeats === 1 ? 'seat' : 'seats'}
      {subscription.nextBillingAmount != null &&
        ` · ${formatUsd(subscription.nextBillingAmount)} ${formatBillingPeriod(subscription.period)}`}
    </p>
    <p className="m-0 mt-1 text-sm text-neutral-600 dark:text-neutral-400">
      Nothing changes today. Your plan renews automatically at your current price, billed to your
      saved payment method.
    </p>
  </div>
);

const ChangePreview = ({
  checkout,
  agencyUser
}: {
  checkout: ReturnType<typeof useCheckoutPreview>;
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) => {
  if (checkout.error) {
    return (
      <div className="mt-8">
        <ErrorMessage>{checkout.error}</ErrorMessage>
        <Button
          color="white"
          className="mt-2"
          disabled={checkout.loading}
          onClick={checkout.reload}
        >
          Retry
        </Button>
      </div>
    );
  }

  const invoicePreview = checkout.preview?.invoicePreview;
  return (
    <div className="mt-8 space-y-3">
      <p className="m-0 text-sm text-neutral-600 dark:text-neutral-400">
        Changes take effect today. Any difference from your current plan is charged or credited now,
        prorated for the rest of your billing period.
      </p>
      {invoicePreview ? (
        <div className="relative" aria-busy={checkout.loading}>
          <div className={clsx(checkout.loading && 'opacity-40')}>
            <InvoicePreview invoicePreview={invoicePreview} agencyUser={agencyUser} compact />
          </div>
          {checkout.loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner color="neutral" size="size-6" />
            </div>
          )}
        </div>
      ) : (
        <InvoicePreviewSkeleton compact />
      )}
    </div>
  );
};

const TermsCheckbox = ({
  checked,
  onChange
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="mt-6">
    <CheckboxField>
      <Checkbox color="sky" checked={checked} onChange={onChange} />
      <Label>
        I agree to renew for a 12 month term under the{' '}
        <a
          href={STANDARD_PLAN_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          className={LINK_CLASS}
        >
          Cara terms
        </a>
        .
      </Label>
    </CheckboxField>
  </div>
);

const QuestionsFooter = () => (
  <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
    Want to talk it through?{' '}
    <a href={RENEWAL_CALL_URL} target="_blank" rel="noopener noreferrer" className="underline">
      Book a meeting
    </a>{' '}
    to discuss your renewal with our support team.
  </p>
);
